import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import {
  VStack,
  Card,
  Heading,
  Text,
  Button,
  Banner,
  Skeleton
} from 'wss3-forge'
import { Building20Regular, Checkmark20Regular } from '@fluentui/react-icons'

export function InviteAccept() {
  const navigate = useNavigate()
  const { token } = useParams()
  const { user, loading: authLoading } = useAuth()

  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ orgName: string; orgSlug: string } | null>(null)

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        // Redirect to login with return URL
        sessionStorage.setItem('invite_token', token || '')
        navigate(`/login?redirect=${encodeURIComponent(`/invite/${token}`)}`)
      } else {
        setLoading(false)
      }
    }
  }, [user, authLoading, token, navigate])

  const handleAccept = async () => {
    if (!token) {
      setError('Invalid invitation token')
      return
    }

    setAccepting(true)
    setError(null)

    try {
      const { data, error } = await supabase.rpc('accept_org_admin_invite', {
        p_token: token
      })

      if (error) throw error

      if (!data.success) {
        setError(data.error || 'Unable to accept invitation')
        return
      }

      setSuccess({
        orgName: data.organization_name,
        orgSlug: data.organization_slug
      })
    } catch (err: any) {
      console.error('Error accepting invite:', err)
      setError(err.message || 'Error accepting invitation')
    } finally {
      setAccepting(false)
    }
  }

  if (loading || authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <Card padding="xl" style={{ maxWidth: '400px', width: '100%' }}>
          <VStack gap="md" style={{ alignItems: 'center' }}>
            <Skeleton width={60} height={60} />
            <Skeleton width={200} height={24} />
            <Skeleton width={150} height={16} />
          </VStack>
        </Card>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-primary)',
      padding: '2rem'
    }}>
      <Card padding="xl" style={{ maxWidth: '400px', width: '100%' }}>
        {success ? (
          <VStack gap="lg" style={{ alignItems: 'center', textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#10B98115',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Checkmark20Regular style={{ fontSize: '32px', color: '#10B981' }} />
            </div>

            <div>
              <Heading level={3}>Welcome!</Heading>
              <Text color="muted">
                You are now an administrator of <strong>{success.orgName}</strong>
              </Text>
            </div>

            <Button
              fullWidth
              onClick={() => navigate(`/org/${success.orgSlug}`)}
            >
              Go to Portal
            </Button>
          </VStack>
        ) : (
          <VStack gap="lg" style={{ alignItems: 'center', textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'var(--brand-primary-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Building20Regular style={{ fontSize: '32px', color: 'var(--brand-primary)' }} />
            </div>

            <div>
              <Heading level={3}>Invitation Received</Heading>
              <Text color="muted">
                You have been invited to become an administrator of an organization.
              </Text>
            </div>

            {error && (
              <Banner variant="error" title="Error">
                {error}
              </Banner>
            )}

            <VStack gap="sm" style={{ width: '100%' }}>
              <Text size="sm" color="muted">
                Signed in as: <strong>{user?.email}</strong>
              </Text>

              <Button
                fullWidth
                onClick={handleAccept}
                loading={accepting}
              >
                Accept Invitation
              </Button>

              <Link to="/" style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                Cancel
              </Link>
            </VStack>
          </VStack>
        )}
      </Card>
    </div>
  )
}
